import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtErr } from "../api";
import Seo from "../components/Seo";
import { Newspaper, AlertTriangle, Fuel, BookOpen, ExternalLink, RefreshCw, Clock } from "lucide-react";

const SOURCE_LABELS = {
  news: "GOV.UK / DfT · European Commission",
  driver_rules: "GOV.UK / DVSA",
  floods: "UK Environment Agency",
  fuel: "European Commission — Weekly Oil Bulletin",
  traffic: "National Highways WebTRIS",
};

function useResource(kind) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/resources/${kind}`).then((r) => setData(r.data)).catch((e) => setData({ status: "error", error: fmtErr(e) }));
  }, [kind]);
  return data;
}

function Shell({ kind, title, sub, icon: Icon, testid, children, seoDesc }) {
  const data = useResource(kind);
  const loading = !data;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid={testid}>
      <Seo title={`${title} | CourierRadar`} description={seoDesc} canonical={`https://courierradar.com/resources/${testid.replace("resource-", "")}`} />
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-2xl bg-[#1C1B1A] flex items-center justify-center"><Icon className="w-5 h-5 text-[#F2D06B]" /></span>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">{title}</h1>
      </div>
      <p className="text-xs text-[#78756E] mb-5 leading-relaxed max-w-xl">{sub}</p>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#A09D95] mb-6" data-testid={`${testid}-meta`}>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />
          {loading ? "Loading…" : data?.fetched_at ? `Last updated ${new Date(data.fetched_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : "—"}</span>
        <span>· Source: <a href={data?.source_url || "#"} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-[#1C1B1A]">{SOURCE_LABELS[kind]}</a></span>
        {data?.status === "error" && <span className="cr-pill-outline !text-[#A09D95]">Feed temporarily unavailable — retrying automatically</span>}
      </div>
      {loading && <p className="text-xs text-[#A09D95] py-16 text-center">Fetching the latest data from the source…</p>}
      {!loading && data?.status === "error" && !data?.data && (
        <div className="cr-card !p-8 text-center" data-testid={`${testid}-unavailable`}>
          <RefreshCw className="w-5 h-5 text-[#A09D95] mx-auto mb-2" />
          <p className="text-sm font-semibold">This feed is temporarily unavailable</p>
          <p className="text-xs text-[#78756E] mt-1 max-w-sm mx-auto">We pull this data directly from the official source and never show invented information. Check the source directly:</p>
          <a href={SOURCES_LINKS[kind]} target="_blank" rel="noreferrer" className="cr-btn-dark mt-4 inline-flex items-center gap-1.5">Open official source <ExternalLink className="w-3.5 h-3.5" /></a>
        </div>
      )}
      {!loading && data?.data && children(data.data)}
    </div>
  );
}

const SOURCES_LINKS = {
  news: "https://www.gov.uk/government/organisations/department-for-transport",
  driver_rules: "https://www.gov.uk",
  floods: "https://check-for-flooding.service.gov.uk/",
  fuel: "https://energy.ec.europa.eu/data-and-analysis/weekly-oil-bulletin_en",
  traffic: "https://webtris.highwaysengland.co.uk/",
};

const NewsList = ({ items, testid }) => (
  <div className="space-y-2.5" data-testid={testid}>
    {items.map((i) => (
      <a key={i.uid} href={i.link} target="_blank" rel="noreferrer" data-testid={`news-item-${i.uid.slice(-8)}`}
        className="cr-card cr-card-hover !p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-snug">{i.title}</p>
          {i.summary && <p className="text-xs text-[#78756E] mt-1 leading-relaxed line-clamp-2">{i.summary}</p>}
          <p className="text-[10px] text-[#A09D95] mt-2">{i.source}{i.published ? ` · ${String(i.published).slice(0, 10)}` : ""}</p>
        </div>
        <ExternalLink className="w-4 h-4 text-[#C9C5B8] shrink-0 mt-1" />
      </a>
    ))}
    {items.length === 0 && <p className="text-xs text-[#A09D95] py-10 text-center">No items published recently.</p>}
  </div>
);

export function ResourceNewsPage() {
  return (
    <Shell kind="news" title="Transport News" icon={Newspaper} testid="resource-news"
      sub="Official transport announcements from the UK Department for Transport and the European Commission's Mobility & Transport directorate."
      seoDesc="Latest official UK DfT and European Commission transport news for carriers and shippers.">
      {(d) => <NewsList items={d.items || []} testid="news-list" />}
    </Shell>
  );
}

const SEVERITY_STYLE = {
  1: "cr-pill-dark", 2: "cr-pill-dark", 3: "cr-pill-yellow", 4: "cr-pill-outline",
};

export function ResourceRoadAlertsPage() {
  const [traffic, setTraffic] = useState(null);
  const [gated, setGated] = useState([]);
  useEffect(() => {
    api.get("/resources/traffic").then((r) => setTraffic(r.data)).catch(() => setTraffic({ status: "error" }));
    api.get("/resources/status").then((r) => setGated(r.data.gated || [])).catch(() => {});
  }, []);
  return (
    <Shell kind="floods" title="Road Alerts" icon={AlertTriangle} testid="resource-road-alerts"
      sub="Live flood warnings that can close or disrupt UK roads, straight from the Environment Agency, plus National Highways traffic-flow data.">
      {(d) => (
        <div className="space-y-6">
          <div className="space-y-2.5" data-testid="flood-list">
            {(d.items || []).map((i) => (
              <a key={i.uid} href={i.link} target="_blank" rel="noreferrer" className="cr-card cr-card-hover !p-4 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold">{i.description}</p>
                    <span className={SEVERITY_STYLE[i.severity_level] || "cr-pill-outline"}>{i.severity}</span>
                  </div>
                  <p className="text-[10px] text-[#A09D95] mt-1.5">{i.area}{i.time_raised ? ` · raised ${String(i.time_raised).replace("T", " ").slice(0, 16)}` : ""}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-[#C9C5B8] shrink-0 mt-1" />
              </a>
            ))}
            {(d.items || []).length === 0 && (
              <div className="cr-card !p-8 text-center" data-testid="floods-clear">
                <p className="text-sm font-semibold">No active flood warnings</p>
                <p className="text-xs text-[#78756E] mt-1">The Environment Agency reports no flood alerts right now. Clear roads.</p>
              </div>
            )}
          </div>

          <section data-testid="traffic-flow">
            <h2 className="text-sm font-bold mb-2">Motorway traffic flow (National Highways WebTRIS)</h2>
            {traffic?.status === "ok" && traffic.data?.items?.length > 0 ? (
              <div className="cr-card !p-0 cr-table-wrap">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
                    <th className="p-3 font-semibold">Monitoring site</th><th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold text-right">Total vehicles</th>
                  </tr></thead>
                  <tbody>
                    {traffic.data.items.map((i) => (
                      <tr key={i.uid} className="border-b border-[#F6F3EA] last:border-0">
                        <td className="p-3">{i.site}</td><td className="p-3 text-[#78756E]">{i.date}</td>
                        <td className="p-3 text-right font-semibold">{i.total_flow?.toLocaleString?.() ?? i.total_flow}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-[#A09D95] cr-card !p-4" data-testid="traffic-unavailable">
                {traffic ? "Traffic flow feed temporarily unavailable — we only show real readings from WebTRIS. Check webtris.highwaysengland.co.uk directly." : "Loading traffic data…"}
              </p>
            )}
          </section>

          <section data-testid="pending-feeds">
            <h2 className="text-sm font-bold mb-2">More official feeds</h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {gated.map((g) => (
                <div key={g.key} className="cr-card !p-4" data-testid={`pending-${g.key}`}>
                  <p className="text-xs font-bold">{g.name}</p>
                  <p className="text-[11px] text-[#78756E] mt-1 leading-relaxed">{g.desc}</p>
                  {!g.configured && <span className="cr-pill-outline !text-[#A09D95] mt-2 inline-block">Pending connection</span>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </Shell>
  );
}

export function ResourceFuelPage() {
  return (
    <Shell kind="fuel" title="Fuel Prices" icon={Fuel} testid="resource-fuel"
      sub="Official EU pump prices from the European Commission's Weekly Oil Bulletin — per litre, including taxes and duties, updated every week.">
      {(d) => (
        <div>
          <p className="text-[11px] text-[#A09D95] mb-3" data-testid="fuel-week">Bulletin week {d.week} · {d.unit} · {d.currency}</p>
          <div className="cr-card !p-0 cr-table-wrap" data-testid="fuel-table">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[#A09D95] border-b border-[#E8E4D9]">
                <th className="p-3 font-semibold">Country</th>
                <th className="p-3 font-semibold text-right">Euro-Super 95 (€/L)</th>
                <th className="p-3 font-semibold text-right">Diesel (€/L)</th>
              </tr></thead>
              <tbody>
                {(d.countries || []).map((c) => (
                  <tr key={c.country} className="border-b border-[#F6F3EA] last:border-0 hover:bg-[#FCFAF5] transition-colors" data-testid={`fuel-${c.country.toLowerCase().replace(/\s/g, "-")}`}>
                    <td className="p-3 font-medium">
                      <Link to={`/resources/fuel-prices/${c.country.toLowerCase().replace(/\s+/g, "-")}`} className="hover:underline underline-offset-2">{c.country}</Link>
                    </td>
                    <td className="p-3 text-right">{c.petrol ?? "—"}</td>
                    <td className="p-3 text-right">{c.diesel ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Shell>
  );
}

export function ResourceFuelCountryPage() {
  const { country } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/resources/fuel").then((r) => setData(r.data)).catch(() => setData({ status: "error" }));
  }, []);
  const pretty = (country || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const entry = (data?.data?.countries || []).find((c) => c.country.toLowerCase() === pretty.toLowerCase());
  const all = data?.data?.countries || [];
  const diesels = all.map((c) => c.diesel).filter((v) => v != null);
  const petrols = all.map((c) => c.petrol).filter((v) => v != null);
  const avg = (arr) => arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="resource-fuel-country">
      <Seo title={`Fuel prices in ${pretty} — diesel & petrol today | CourierRadar`}
        description={`Official fuel prices in ${pretty}: diesel and Euro-Super 95 petrol per litre incl. taxes, from the European Commission Weekly Oil Bulletin. Updated weekly.`}
        canonical={`https://courierradar.com/resources/fuel-prices/${country}`} />
      <p className="text-[11px] text-[#A09D95] mb-2"><Link to="/resources/fuel-prices" className="underline underline-offset-2">← All fuel prices</Link></p>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Fuel prices in {pretty}</h1>
      <p className="text-xs text-[#78756E] mb-6 max-w-lg leading-relaxed">
        Official pump prices for {pretty} from the European Commission's Weekly Oil Bulletin — per litre, including taxes and duties.
        Essential numbers for quoting long-distance work.
      </p>
      {!data && <p className="text-xs text-[#A09D95] py-16 text-center">Loading official data…</p>}
      {data && !entry && <p className="text-xs text-[#A09D95] py-16 text-center">No official bulletin data for this country yet. <Link to="/resources/fuel-prices" className="underline">See all countries</Link>.</p>}
      {entry && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-6" data-testid="fuel-country-cards">
            <div className="cr-card !p-6 text-center">
              <p className="cr-label !text-[#A09D95] mb-1">Diesel</p>
              <p className="font-heading text-3xl font-bold">{entry.diesel != null ? `€${entry.diesel}` : "—"}</p>
              <p className="text-[10px] text-[#A09D95] mt-1">per litre, incl. taxes & duties</p>
              {avg(diesels) && entry.diesel != null && (
                <p className="text-[11px] mt-2 font-semibold">{entry.diesel < avg(diesels) ? "Below" : "Above"} EU average (€{avg(diesels).toFixed(3)})</p>
              )}
            </div>
            <div className="cr-card !p-6 text-center">
              <p className="cr-label !text-[#A09D95] mb-1">Euro-Super 95</p>
              <p className="font-heading text-3xl font-bold">{entry.petrol != null ? `€${entry.petrol}` : "—"}</p>
              <p className="text-[10px] text-[#A09D95] mt-1">per litre, incl. taxes & duties</p>
              {avg(petrols) && entry.petrol != null && (
                <p className="text-[11px] mt-2 font-semibold">{entry.petrol < avg(petrols) ? "Below" : "Above"} EU average (€{avg(petrols).toFixed(3)})</p>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[#A09D95] mb-6">Bulletin week {data.data.week} · Source: <a href="https://energy.ec.europa.eu/data-and-analysis/weekly-oil-bulletin_en" target="_blank" rel="noreferrer" className="underline underline-offset-2">European Commission — Weekly Oil Bulletin</a> · Last updated {new Date(data.fetched_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
          <h2 className="text-sm font-bold mb-2">Compare with neighbours</h2>
          <div className="flex flex-wrap gap-2" data-testid="fuel-country-links">
            {all.filter((c) => c.country !== entry.country).slice(0, 14).map((c) => (
              <Link key={c.country} to={`/resources/fuel-prices/${c.country.toLowerCase().replace(/\s+/g, "-")}`}
                className="cr-pill-outline hover:border-[#1C1B1A]/30 transition-colors !px-3 !py-1">{c.country}</Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function ResourceDriverRulesPage() {
  return (
    <Shell kind="driver_rules" title="Driver Rules" icon={BookOpen} testid="resource-driver-rules"
      sub="Current official guidance for professional drivers — drivers' hours, tachographs, CPC and compliance — from GOV.UK and the DVSA.">
      {(d) => <NewsList items={d.items || []} testid="rules-list" />}
    </Shell>
  );
}
